// Wishlist.tsx
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Wishlist.css';
import CourseCard from '@/Components/CourseCard/CourseCard';
import { useInstructorLearningsStore } from '@/stores/InstructorStore/InstructorLearningsStore';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import { FaRegHeart } from 'react-icons/fa';

const Wishlist: React.FC = () => {
  const navigate = useNavigate();
  const {
    wishlistCourses,
    loadingWishlist,
    errorWishlist,
    savedCourseIds,
    fetchWishlistCourses,
    fetchSavedCourseIds,
    toggleSaveCourse,
  } = useInstructorLearningsStore();

  // Fetch data on mount
  useEffect(() => {
    fetchWishlistCourses();
    fetchSavedCourseIds(); // Sync saved IDs with wishlist courses
  }, [fetchWishlistCourses, fetchSavedCourseIds]);

  // Handle unsave course from wishlist
  const handleUnsave = async (courseId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation to course detail

    try {
      await toggleSaveCourse(courseId);
      // UI will update automatically when store refreshes wishlist
    } catch (error: any) {
      console.error('Failed to unsave course:', error);
      // Error feedback can be added here (toast, snackbar, etc.)
    }
  };

  // Loading state
  if (loadingWishlist) {
    return (
      <LoadingSpinner
        fullScreen
        size="lg"
      />
    );
  }

  // Error state
  if (errorWishlist) {
    return (
      <ErrorDisplay
        fullScreen
        title="Failed to Load Wishlist"
        message={errorWishlist}
        type="error"
        showRetry
        onRetry={fetchWishlistCourses}
      />
    );
  }

  // Empty state
  if (!wishlistCourses || wishlistCourses.length === 0) {
    return (
      <div className="wishlist-container">
        <div className="wishlist-header">
          <h1>My Wishlist</h1>
          <p>Save your favorite courses and enroll when you're ready</p>
        </div>
        <div className="wishlist-empty-state">
          <FaRegHeart size={64} />
          <h3>Your Wishlist is Empty</h3>
          <p>Browse courses and save the ones you're interested in!</p>
          <button className="wishlist-browse-btn" onClick={() => navigate('/trainer/my-learnings/browse')}>
            Browse Courses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="wishlist-container">
      <div className="wishlist-header">
        <h1>My Wishlist</h1>
        <p>Save your favorite courses and enroll when you're ready</p>
      </div>
      <div className="wishlist-grid">
        {wishlistCourses?.map((course) => (
          <CourseCard
            key={course.id}
            course={course}
            isSaved={savedCourseIds.has(course.id)}
            onToggleSave={(e) => handleUnsave(course.id, e)}
          />
        ))}
      </div>
    </div>
  );
};

export default Wishlist;
